import moment from "moment-timezone";
import { formatNumberToDisplay } from "@/lib/api/common";
import { CommonDestinationTimezone } from "@/lib/api/utils";
import type {
  OtcLayerInvoice,
  OtcLayerInvoiceFeeInfo,
  OtcLayerInvoiceQueueEntry,
} from "@/lib/api/otc";

function fmtDate(value: string | null): string {
  if (!value) return "—";
  const m = moment.tz(value, CommonDestinationTimezone);
  return m.isValid() ? m.format("MM/DD/YYYY") : "—";
}

function LegacyInvoiceFeeInfo({ info }: { info: OtcLayerInvoiceFeeInfo }) {
  return (
    <div className="grid grid-cols-2 md:grid-cols-4 gap-x-6 gap-y-2">
      <div className="flex flex-col">
        <span className="text-xs text-muted-foreground">Paid</span>
        <span className="text-sm">{info.has_been_paid ? "Yes" : "No"}</span>
      </div>
      <div className="flex flex-col">
        <span className="text-xs text-muted-foreground">Invoice #</span>
        <span className="text-sm">
          {info.invoice_number.trim() !== "" ? info.invoice_number.trim() : "—"}
        </span>
      </div>
      <div className="flex flex-col">
        <span className="text-xs text-muted-foreground">Invoice Date</span>
        <span className="text-sm">{fmtDate(info.invoice_date)}</span>
      </div>
      <div className="flex flex-col">
        <span className="text-xs text-muted-foreground">Date Marked</span>
        <span className="text-sm">{fmtDate(info.date_marked)}</span>
      </div>
    </div>
  );
}

function QueuedInvoiceInfo({ entry }: { entry: OtcLayerInvoiceQueueEntry }) {
  return (
    <div className="flex flex-col gap-2">
      <span className="text-xs font-medium text-muted-foreground">Queued Invoice</span>
      <div className="flex flex-col gap-1">
        <span className="text-xs text-muted-foreground">Allocations</span>
        {entry.allocations.length === 0 ? (
          <span className="text-sm">—</span>
        ) : (
          entry.allocations.map(
            (allocation: OtcLayerInvoiceQueueEntry["allocations"][number]) => (
              <span key={allocation.id} className="text-sm">
                {allocation.Customer.long_name}: {formatNumberToDisplay(allocation.percentage, {
                  maximumFractionDigits: 4,
                  suffix: "%",
                })}
              </span>
            ),
          )
        )}
      </div>
    </div>
  );
}

function SentInvoiceInfo({ invoices }: { invoices: OtcLayerInvoice[] }) {
  return (
    <div className="flex flex-col gap-2">
      <span className="text-xs font-medium text-muted-foreground">Invoices</span>
      {invoices.map((invoice: OtcLayerInvoice) => (
        <div
          key={invoice.id}
          className="grid grid-cols-2 md:grid-cols-5 gap-x-6 gap-y-2 border-t pt-2 first:border-t-0 first:pt-0"
        >
          <div className="flex flex-col">
            <span className="text-xs text-muted-foreground">Customer</span>
            <span className="text-sm">{invoice.Customer.long_name}</span>
          </div>
          <div className="flex flex-col">
            <span className="text-xs text-muted-foreground">Allocation</span>
            <span className="text-sm">
              {formatNumberToDisplay(invoice.allocation_percentage, {
                maximumFractionDigits: 4,
                suffix: "%",
              })}
            </span>
          </div>
          <div className="flex flex-col">
            <span className="text-xs text-muted-foreground">Invoice #</span>
            <span className="text-sm">{invoice.invoice_number}</span>
          </div>
          <div className="flex flex-col">
            <span className="text-xs text-muted-foreground">Invoice Date</span>
            <span className="text-sm">{fmtDate(invoice.invoice_date)}</span>
          </div>
          <div className="flex flex-col">
            <span className="text-xs text-muted-foreground">Final Amount</span>
            <span className="text-sm">
              {formatNumberToDisplay(invoice.final_invoice_amount, { isCurrency: true })}
            </span>
          </div>
        </div>
      ))}
    </div>
  );
}

export function LayerInvoiceFeeInfo({
  info,
  latestQueueEntry,
  invoices,
}: {
  info: OtcLayerInvoiceFeeInfo;
  latestQueueEntry: OtcLayerInvoiceQueueEntry | null;
  invoices: OtcLayerInvoice[];
}) {
  const hasLegacy = !!info.invoice_number;
  const hasSentInvoices = (invoices?.length ?? 0) > 0;
  const hasCurrentInvoiceInformation = latestQueueEntry != null || hasSentInvoices || (!hasLegacy);

  return (
    <div className="flex flex-col gap-3">
      {(hasSentInvoices || !!latestQueueEntry || hasLegacy) && (
        <span className="text-xs font-semibold uppercase text-muted-foreground">
            Invoice Info
        </span>
      )}
      {latestQueueEntry && <QueuedInvoiceInfo entry={latestQueueEntry} />}
      {hasSentInvoices && <SentInvoiceInfo invoices={invoices} />}
      {!hasCurrentInvoiceInformation && <LegacyInvoiceFeeInfo info={info} />}
    </div>
  );
}
